import { createApiClient } from '@chikara/utils';
import { API_BASE_URL, APP_CONFIG } from '@/config';
import { ApiError, BackendErrorPayload } from '@chikara/types';
import { mapApiError } from '@/features/auth/utils/mapApiError';
import { tokenStorage } from '@/features/auth/utils/tokenStorage';
import { API_ENDPOINTS } from '@/config';
import { trackError } from '@/shared/analytics/errors';

const TOKEN_KEY = APP_CONFIG.session.tokenKey;
const REFRESH_TOKEN_KEY = APP_CONFIG.session.refreshTokenKey;

function getRealAccessToken(): string | null {
  const token = tokenStorage.getAccessTokenOptional();
  if (!token) return null;
  // Never treat mock tokens as "real" for Authorization
  if (token.endsWith('.mock_signature')) return null;
  return token;
}

const PUBLIC_ENDPOINTS = new Set<string>([
  API_ENDPOINTS.auth.login,
  API_ENDPOINTS.auth.preLogin,
  API_ENDPOINTS.auth.refreshToken,
  API_ENDPOINTS.auth.register,
  API_ENDPOINTS.auth.forgotPassword,
  API_ENDPOINTS.auth.resetPassword,
]);

/**
 * Configured API client for the platform app.
 * All services should use this instance for consistent configuration.
 */
export const apiClient = createApiClient({
  baseUrl: API_BASE_URL,
  getToken: () => getRealAccessToken(),
  shouldAttachAuth: (endpoint) => !PUBLIC_ENDPOINTS.has(endpoint),
  onUnauthorized: () => {
    // Clear tokens on 401 response
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    // Optionally redirect to login or emit an event
    window.dispatchEvent(new CustomEvent('auth:unauthorized'));
  },
  onError: async (response: any) => {
    let backendPayload: BackendErrorPayload | undefined;

    try {
      backendPayload = await response.json();
    } catch {
      // response without body
    }
  
    const apiError = mapApiError(backendPayload);
  
    window.dispatchEvent(
      new CustomEvent<ApiError>('api:error', { detail: apiError })
    );
  
    if (response.status === 401) {
      window.dispatchEvent(new Event('auth:unauthorized'));
    }
  
    trackError({
      source: 'apiClient',
      category: 'api',
      error: apiError,
      context: {
        httpStatus: response.status,
      },
    });

    throw apiError;
  },
  onNetworkError: (error: unknown) => {
    const apiError: ApiError = {
      type: 'network',
      status: 400,
      message:
        error instanceof Error
          ? error.message
          : 'Network error',
    };

    window.dispatchEvent(
      new CustomEvent<ApiError>('api:error', { detail: apiError })
    );

    trackError({
      source: 'apiClient',
      category: 'network',
      error: apiError,
    });

    throw apiError;
  },
});
